import classNames from "classnames";

import * as Color from "../Color";
import { IconSize } from "../Icon";
import { ButtonSize } from "./ButtonSize";

/**
 * The button label appearance.
 */
export interface ButtonLabelAppearance {
  /**
   * The font size.
   */
  readonly fontSize: number;

  /**
   * The line height.
   */
  readonly lineHeight: number;

  /**
   * The letter spacing.
   */
  readonly letterSpacing: number;

  /**
   * Indicates whether to render the label text in uppercase.
   */
  readonly uppercase: boolean;

  /**
   * Indicates the weight of the button text.
   */
  readonly weight: "bold" | "semibold" | "regular";
}

/** The button icon appearance. */
export interface ButtonIconAppearance {
  /** The icon size. */
  readonly size: IconSize;
}

/**
 * The button padding appearance.
 */
export interface ButtonPaddingAppearance {
  /**
   * The number of pixels to pad by vertically.
   */
  readonly vertical: number;

  /**
   * The number of pixels to pad by horizontally.
   * If the button only has an icon this is ignored and horizontal padding is made equal to vertical padding.
   */
  readonly horizontal: number;
}

/**
 * The appearance of a button state.
 */
export interface ButtonStateAppearance {
  /**
   * The button background color.
   */
  readonly backgroundColor: Color.RgbColor;

  /**
   * The button border color. If not specified defaults to the background color.
   */
  readonly borderColor?: Color.RgbColor;

  /**
   * The button foreground color.
   */
  readonly foregroundColor: Color.RgbColor;

  /**
   * Indicates whether to underline text. If not specified defaults to false.
   */
  readonly textUnderline?: boolean;
}

/**
 * A button appearance.
 */
export interface ButtonAppearance {
  /**
   * The CSS class name to apply to the button.
   */
  readonly className?: string;

  /**
   * The label appearance.
   */
  readonly label: ButtonLabelAppearance;

  /** The icon appearance. */
  readonly icon: ButtonIconAppearance;

  /**
   * The button padding.
   */
  readonly padding: ButtonPaddingAppearance;

  /**
   * The appearance when normal.
   */
  readonly normal: ButtonStateAppearance;

  /**
   * The appearance when hovered.
   */
  readonly hover: ButtonStateAppearance;

  /**
   * The appearance when pressed.
   */
  readonly pressed: ButtonStateAppearance;

  /**
   * The appearance when disabled.
   */
  readonly disabled: ButtonStateAppearance;
}

/**
 * A tertiary button appearance.
 */
export interface ButtonAppearanceTertiary extends ButtonAppearance {
  /**
   * An unpadded variant of the tertiary button style.
   */
  readonly Unpadded: ButtonAppearance;
}

/**
 * The set of button sizes.
 */
export interface ButtonVariant {
  /**
   * Large button size.
   */
  readonly Large: ButtonAppearance;

  /**
   * Small button size.
   */
  readonly Small: ButtonAppearance;

  /**
   * Compact button size.
   */
  readonly Compact: ButtonAppearance;
}

/**
 * The tertiary button variant.
 */
export interface ButtonVariantTertiary {
  /**
   * Large button size.
   */
  readonly Large: ButtonAppearanceTertiary;

  /**
   * Small button size.
   */
  readonly Small: ButtonAppearanceTertiary;

  /**
   * Compact button size.
   */
  readonly Compact: ButtonAppearanceTertiary;
}

/**
 * A button type.
 */
export interface ButtonType {
  /**
   * The primary button variant.
   */
  readonly Primary: ButtonVariant;

  /**
   * The secondary button variant.
   */
  readonly Secondary: ButtonVariant;

  /**
   * The tertiary button variant.
   */
  readonly Tertiary: ButtonVariantTertiary;
}

/**
 * The set of button variants.
 */
export interface ButtonTypes {
  /**
   * The functional button variant.
   */
  readonly Functional: ButtonType;

  /**
   * The destructive button variant.
   */
  readonly Destructive: ButtonType;
}

/*
  TODO: The actual implementation for appearances should probably exist in a separate
  TODO: branding library to allow for the easy hot swapping of branding appearances
  TODO: which could even be automated by the shell if we have a formal "branding"
  TODO: library interface that all branding libraries must conform to (exported types, etc.)

  TODO: For now though we'll just implement it here.
*/

/**
 * Creates the button padding.
 * @param size The button size.
 * @returns The padding associated with the size.
 */
function createPadding(size: ButtonSize): ButtonPaddingAppearance {
  switch (size) {
    default:
    case "large":
      return { horizontal: 11, vertical: 7 };
    case "small":
      return { horizontal: 11, vertical: 5 };
    case "compact":
      return { horizontal: 11, vertical: 3 };
  }
}

function createLabel(size: ButtonSize): ButtonLabelAppearance {
  switch (size) {
    default:
    case "large":
      return { fontSize: 15, lineHeight: 20, letterSpacing: 0, uppercase: false, weight: "semibold" };
    case "small":
    case "compact":
      return { fontSize: 14, lineHeight: 16, letterSpacing: 0, uppercase: false, weight: "semibold" };
  }
}

function createPrimaryAppearance(
  className: string,
  size: ButtonSize,
  baseColor: Color.RgbColor,
  disabledColor: Color.RgbColor,
): ButtonAppearance {
  const padding = createPadding(size);
  const label = createLabel(size);

  const { l: baseLightness } = Color.toHsl(baseColor);
  const foregroundColor = baseLightness < 0.5 ? Color.White : Color.Black;

  return {
    className: classNames(className, "primary", size),
    label,
    icon: {
      size: "regular",
    },
    padding,
    normal: {
      backgroundColor: baseColor,
      borderColor: baseColor,
      foregroundColor,
    },
    hover: {
      backgroundColor: Color.lighten(baseColor, 0.04),
      borderColor: Color.lighten(baseColor, 0.04),
      foregroundColor,
    },
    pressed: {
      backgroundColor: Color.darken(baseColor, 0.06),
      borderColor: Color.darken(baseColor, 0.06),
      foregroundColor,
    },
    disabled: {
      backgroundColor: disabledColor,
      borderColor: disabledColor,
      foregroundColor,
    },
  };
}

function createSecondaryAppearance(
  className: string,
  size: ButtonSize,
  baseColor: Color.RgbColor,
  disabledColor: Color.RgbColor,
): ButtonAppearance {
  const padding = createPadding(size);
  const label = createLabel(size);

  return {
    className: classNames(className, "secondary", size),
    label,
    icon: {
      size: "regular",
    },
    padding,
    normal: {
      backgroundColor: Color.Transparent,
      borderColor: baseColor,
      foregroundColor: baseColor,
    },
    hover: {
      backgroundColor: Color.withAlpha(Color.lighten(baseColor, 0.06), 0.1),
      borderColor: Color.lighten(baseColor, 0.06),
      foregroundColor: Color.lighten(baseColor, 0.06),
    },
    pressed: {
      backgroundColor: Color.withAlpha(Color.darken(baseColor, 0.06), 0.1),
      borderColor: Color.darken(baseColor, 0.06),
      foregroundColor: Color.darken(baseColor, 0.06),
    },
    disabled: {
      backgroundColor: Color.withAlpha(disabledColor, 0.1),
      borderColor: disabledColor,
      foregroundColor: disabledColor,
    },
  };
}

function createTertiaryAppearance(
  className: string,
  size: ButtonSize,
  baseColor: Color.RgbColor,
  disabledColor: Color.RgbColor,
): ButtonAppearanceTertiary {
  const padding = createPadding(size);
  const label = createLabel(size);

  const appearanceBase: ButtonAppearance = {
    className: classNames(className, "tertiary", size),
    label,
    icon: {
      size: "regular",
    },
    padding: {
      horizontal: 0,
      vertical: padding.vertical,
    },
    normal: {
      backgroundColor: Color.Transparent,
      borderColor: Color.Transparent,
      foregroundColor: baseColor,
    },
    hover: {
      backgroundColor: Color.Transparent,
      borderColor: Color.Transparent,
      foregroundColor: Color.lighten(baseColor, 0.03),
      textUnderline: true,
    },
    pressed: {
      backgroundColor: Color.Transparent,
      borderColor: Color.Transparent,
      foregroundColor: Color.darken(baseColor, 0.06),
      textUnderline: true,
    },
    disabled: {
      backgroundColor: Color.Transparent,
      borderColor: Color.Transparent,
      foregroundColor: disabledColor,
    },
  };

  return {
    ...appearanceBase,
    Unpadded: {
      ...appearanceBase,
      padding: {
        horizontal: 0,
        vertical: 0,
      },
    },
  };
}

export function createButtonType(className: string, baseColor: Color.RgbColor, disabledColor: Color.RgbColor): ButtonType {
  return {
    Primary: {
      Large: createPrimaryAppearance(className, "large", baseColor, disabledColor),
      Small: createPrimaryAppearance(className, "small", baseColor, disabledColor),
      Compact: createPrimaryAppearance(className, "compact", baseColor, disabledColor),
    },
    Secondary: {
      Large: createSecondaryAppearance(className, "large", baseColor, disabledColor),
      Small: createSecondaryAppearance(className, "small", baseColor, disabledColor),
      Compact: createSecondaryAppearance(className, "compact", baseColor, disabledColor),
    },
    Tertiary: {
      Large: createTertiaryAppearance(className, "large", baseColor, disabledColor),
      Small: createTertiaryAppearance(className, "small", baseColor, disabledColor),
      Compact: createTertiaryAppearance(className, "compact", baseColor, disabledColor),
    },
  };
}

/**
 * The set of available button types.
 */
export const Buttons: ButtonTypes = {
  Functional: createButtonType("functional", Color.Turqoise[500], Color.Turqoise[200]),
  Destructive: createButtonType("destructive", Color.Red[600], Color.fromString("#F2CECE")),
};
